import { Checkbox, Col, Divider, Form, notification, Row } from 'antd';
import { memo, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useMutation, useQueries, useQuery, useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';

import ButtonWrap from '@/components/ButtonWrap';
import CheckBoxGroupWrap from '@/components/CheckBoxGroupWrap';
import CheckBoxWrap from '@/components/CheckBoxWrap';
import FormItemWrap from '@/components/FormItemWrap';
import InputAreaWrap from '@/components/InputAreaWrap';
import InputMoney from '@/components/InputMoney';
import InputWrap from '@/components/InputWrap';
import RadioWrap from '@/components/RadioWrap';
import SelectWrap from '@/components/SelectWrap';
import UploadImage from '@/components/UploadImage';
import { QueryKey } from '@/constants/queryKey';
import { pattern } from '@/constants/validate';
import { LocalFileResponseDTO, ProductForm } from '@/dto/responseDto/local-file.dto';
import { appRoutes } from '@/router/routes';
import categoryService from '@/services/category.service';
import productService from '@/services/product.service';
import { convertOptionSelect } from '@/util/convertOptionSelect';
import { handleMessageError } from '@/util/handleMessageError';
import './style.scss';

const FormDataSanPham = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const {
        control,
        handleSubmit,
        getValues,
        reset,
        setValue,
        formState: { errors },
    } = useForm<ProductForm>();

    const [categories] = useQueries([
        { queryKey: ['category'], queryFn: () => categoryService.getAll({ admin: true }) },
        {},
    ]);

    const { data: product } = useQuery(['san-pham', id], () => productService.findById(id as string), {
        enabled: !!id,
        onError: (error: any) => {
            handleMessageError(error);
        },
    });
    const categoryOpts = convertOptionSelect(categories?.data?.data ?? [], '_id', 'name');

    const { mutate: mutateCreate, isLoading: isLoadingCreate } = useMutation(
        (body: ProductForm) => productService.create(body),
        {
            onSuccess: () => {
                onGoList();
                queryClient.removeQueries('san-pham');
                notification.success({
                    message: 'THÊM SẢN PHẨM THÀNH CÔNG',
                });
            },
            onError: (error: any) => {
                handleMessageError(error);
            },
        },
    );

    const { mutate: mutateUpdate, isLoading: isLoadingUpdate } = useMutation(
        ['san-pham', id],
        (body: ProductForm) => productService.update(id as string, body),
        {
            onSuccess: () => {
                onGoList();
                queryClient.removeQueries('san-pham');
                notification.success({
                    message: 'CẬP NHẬT SẢN PHẨM THÀNH CÔNG',
                });
            },
            onError: (error: any) => {
                handleMessageError(error);
            },
        },
    );

    const onGoList = () => {
        reset({});
        navigate(`${appRoutes.ADMIN.SERVICE.ITEM}`);
    };

    const onSubmit = (data: ProductForm) => {
        const dataSubmit = {
            ...data,
        };

        if (id) {
            mutateUpdate(dataSubmit);
        } else {
            mutateCreate(dataSubmit);
        }
    };

    useEffect(() => {
        if (product) {
            reset({
                id: product.data._id,
                categoryId: product.data.category,
                name: product.data.name,
                salesPrice: product.data.salesPrice,
                image: product.data.image,
            });
        } else {
            reset({});
        }
    }, [product, reset]);

    return (
        <div className="p-add-product__form">
            <Form labelCol={{ span: 6, md: { span: 8 }, lg: { span: 6 } }} onFinish={handleSubmit(onSubmit)}>
                <Row>
                    <Col span={24} xl={{ span: 16, offset: 4 }} xxl={{ span: 12, offset: 6 }}>
                        <FormItemWrap
                            labelAlign="right"
                            label="Danh mục"
                            required
                            colon={false}
                            validateStatus={errors?.categoryId ? 'error' : ''}
                        >
                            <Controller
                                name="categoryId"
                                control={control}
                                defaultValue={getValues('categoryId')}
                                rules={{
                                    required: true,
                                }}
                                render={({ field }) => <SelectWrap options={categoryOpts} field={field} />}
                            />
                        </FormItemWrap>

                        <FormItemWrap
                            labelAlign="right"
                            label="Tên"
                            required
                            colon={false}
                            validateStatus={errors?.name ? 'error' : ''}
                        >
                            <Controller
                                name="name"
                                control={control}
                                rules={{
                                    required: true,
                                    maxLength: 100,
                                }}
                                render={({ field }) => <InputWrap field={field} />}
                            />
                        </FormItemWrap>
                        <FormItemWrap
                            labelAlign="right"
                            label="Giá"
                            required
                            colon={false}
                            validateStatus={errors?.salesPrice ? 'error' : ''}
                        >
                            <Controller
                                name="salesPrice"
                                control={control}
                                rules={{
                                    required: true,
                                    pattern: pattern.inputNumber,
                                }}
                                render={({ field }) => (
                                    <InputMoney
                                        textAlign="right"
                                        addonAfter="₫"
                                        placeholder="0"
                                        maxLength={9}
                                        field={field}
                                    />
                                )}
                            />
                        </FormItemWrap>

                        <FormItemWrap labelAlign="right" label={'Hình ảnh'} colon={false}>
                            <UploadImage
                                photo={product?.data.image}
                                onFileChanges={(image: LocalFileResponseDTO) => setValue('image', image)}
                            />
                        </FormItemWrap>

                        <Row justify="end">
                            <ButtonWrap
                                isLoading={isLoadingCreate || isLoadingUpdate}
                                htmlType="submit"
                                text={id ? 'CẬP NHẬT' : 'THÊM'}
                                type="primary"
                                width={140}
                            />
                        </Row>
                    </Col>
                </Row>
            </Form>
        </div>
    );
};

export default memo(FormDataSanPham);
